import { useAtomValue, useSetAtom } from 'jotai'
import {
  Text,
  Button,
  CustomerLogo,
  GreyGrey80,
  GreyWhite,
  IconBarsThreeBottomLeft,
  IconCalendarDays,
  IconListBullets,
  IconPageText,
  Image,
  Primary13Regular,
  Flex
} from '@sequencehq/core-components'
import stripeLogo from '../../assets/stripeLogo.svg'
import goCardlessLogo from 'Integrations/integrationsConfig/goCardless/goCardlessLogo.svg'
import { Step } from '../../components'
import { currentEditorStepAtom } from '../../editor.atoms'
import { SummaryRow } from './SummaryRow'
import { SummaryGrid, SummaryGridItem } from './SummaryGrid'
import { FinalizeAndSendModal } from 'Invoices/components/CreateEditShared'
import {
  invoiceIdAtom,
  invoiceStatusAtom,
  invoiceCustomerIdAtom,
  humanReadableDatesAtom,
  invoiceMemoAtom,
  lineItemGroupsAtom,
  invoicePurchaseOrderNumberAtom,
  invoiceReferenceAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { useFetchCustomerQuery } from 'Customer/index'
import { getLogoSrc } from 'Customer/utils/logo'
import { totalQuantityForGroup } from 'Invoices/utils/calculation'
import { useCloseInvoiceEditor } from 'Invoices/hooks/useCloseInvoiceEditor'
import { usePaymentProviderForInvoice } from 'Invoices/hooks/usePaymentProviderForInvoice'
import { useHumanReadableTotalAfterCreditGrants } from 'Invoices/hooks/useCreditGrantTotals'
import { useResolvedInvoiceDueDate } from 'Invoices/hooks/useResolvedInvoiceDueDate'
import { DUE_DATE_PENDING_TEXT } from 'Invoices/constants'
import { localDateWithFormat } from '@sequencehq/utils'
import { match } from 'ts-pattern'

const UNSET_VALUE_TEXT: string = 'Not set'

export const StepSummary = () => {
  const customerId = useAtomValue(invoiceCustomerIdAtom)
  const { customerDetails: customer } = useFetchCustomerQuery(customerId ?? '')
  const totalAfterCredit = useHumanReadableTotalAfterCreditGrants()

  if (!customer) return null

  return (
    <Step
      title={`Send invoice for ${totalAfterCredit} to ${customer.legalName}`}
      controls={<Controls />}
    >
      <Step.Section title="Summary">
        <Flex flexDir="column" data-testid="invoiceSummary">
          <CustomerSummaryRow />
          <DateSummaryRows />
          <LineItemsSummaryRow />
          <StripeSummaryRow />
          <GoCardlessSummaryRow />
          <MemoSummaryRow />
          <CustomFieldsRow />
        </Flex>
      </Step.Section>
    </Step>
  )
}

const Controls = () => {
  const closeInvoiceEditor = useCloseInvoiceEditor()

  const setStep = useSetAtom(currentEditorStepAtom)
  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceStatus = useAtomValue(invoiceStatusAtom)

  return (
    <Step.Controls
      back={
        <Step.Controls.Back
          label="Back"
          onClick={() => setStep('TERMS')}
          keyboardShortcut={['Mod', '[']}
        />
      }
      forward={
        invoiceId &&
        invoiceStatus !== 'SENT' && (
          <FinalizeAndSendModal
            trigger={({ onClick, disabled }) => (
              <Button
                variant="primary"
                onClick={onClick}
                disabled={disabled}
                keyboardShortcut={['Mod', 'Enter']}
                data-testid="sendInvoice.button"
              >
                Send invoice
              </Button>
            )}
            onSuccess={() => {
              closeInvoiceEditor()
            }}
          />
        )
      }
    />
  )
}

const CustomerSummaryRow = () => {
  const customerId = useAtomValue(invoiceCustomerIdAtom)
  const { customerDetails: customer } = useFetchCustomerQuery(customerId ?? '')

  if (!customer) {
    return null
  }

  return (
    <SummaryRow
      icon={
        <CustomerLogo src={getLogoSrc(customer)} size={32} fallbackSize={16} />
      }
      title={customer.legalName}
      body={customer.contacts.map(contact => contact.email).join(', ')}
      goToStep="CUSTOMER_AND_LINE_ITEMS"
      data-testid="invoiceSummary.customer"
    />
  )
}

const DateSummaryRows = () => {
  const { billingPeriod } = useAtomValue(humanReadableDatesAtom)
  const dueDateResolution = useResolvedInvoiceDueDate()

  const dueDateBody = match(dueDateResolution)
    .with(undefined, () => UNSET_VALUE_TEXT)
    .with({ type: 'RESOLVED' }, ({ dueDate }) =>
      localDateWithFormat(dueDate, 'd MMMM yyyy')
    )
    // The backend derives the due date on issue, so there is nothing to show yet
    .with({ type: 'PENDING' }, () => DUE_DATE_PENDING_TEXT)
    .exhaustive()

  return (
    <>
      <SummaryRow
        icon={<IconCalendarDays />}
        title="Due date"
        body={dueDateBody}
        goToStep="TERMS"
        data-testid="invoiceSummary.dueDate"
      />

      {dueDateResolution?.type === 'PENDING' && (
        <SummaryRow
          icon={<IconCalendarDays />}
          title="Payment terms"
          body={`Net ${dueDateResolution.paymentTermDays}`}
          goToStep="TERMS"
          data-testid="invoiceSummary.paymentTerms"
        />
      )}

      <SummaryRow
        icon={<IconCalendarDays />}
        title="Billing period"
        body={billingPeriod ?? UNSET_VALUE_TEXT}
        goToStep="TERMS"
        data-testid="invoiceSummary.billingPeriod"
      />
    </>
  )
}

const LineItemsSummaryRow = () => {
  const totalAfterCredit = useHumanReadableTotalAfterCreditGrants()
  const lineItemGroups = useAtomValue(lineItemGroupsAtom)

  return (
    <SummaryRow
      icon={<IconListBullets />}
      title={totalAfterCredit}
      body={
        <Flex flexDir="column">
          {lineItemGroups?.map(lineItemGroup => (
            <Text
              {...Primary13Regular}
              color={GreyGrey80}
              key={lineItemGroup.id}
            >
              {totalQuantityForGroup(lineItemGroup).toLocaleString('en-US')}x{' '}
              {lineItemGroup.title}
            </Text>
          ))}
        </Flex>
      }
      goToStep="CUSTOMER_AND_LINE_ITEMS"
      data-testid="invoiceSummary.total"
    />
  )
}

const MemoSummaryRow = () => {
  const memo = useAtomValue(invoiceMemoAtom)

  return (
    <SummaryRow
      icon={<IconBarsThreeBottomLeft />}
      title="Memo"
      body={memo ? memo : UNSET_VALUE_TEXT}
      goToStep="TERMS"
      data-testid="invoiceSummary.memo"
    />
  )
}

const StripeSummaryRow = () => {
  const paymentProvider = usePaymentProviderForInvoice()

  if (paymentProvider !== 'STRIPE') return null

  return (
    <SummaryRow
      icon={<Image src={stripeLogo} width="100%" height="100%" />}
      title="Stripe"
      body="Recipients can pay with Stripe"
      goToStep="TERMS"
      data-testid="invoiceSummary.stripe"
    />
  )
}

const GoCardlessSummaryRow = () => {
  const paymentProvider = usePaymentProviderForInvoice()

  if (paymentProvider !== 'GOCARDLESS') return null

  return (
    <SummaryRow
      icon={<Image src={goCardlessLogo} width="100%" height="100%" />}
      iconBackground={GreyWhite}
      title="GoCardless"
      body="Recipients can pay with GoCardless"
      goToStep="TERMS"
      data-testid="invoiceSummary.goCardless"
    />
  )
}

const CustomFieldsRow = () => {
  const purchaseOrderNumber = useAtomValue(invoicePurchaseOrderNumberAtom)
  const reference = useAtomValue(invoiceReferenceAtom)

  return (
    <SummaryRow
      hideBorderBottom
      icon={<IconPageText />}
      title="Custom fields"
      body={
        <SummaryGrid>
          <SummaryGridItem
            name="PO Number"
            value={purchaseOrderNumber ? purchaseOrderNumber : UNSET_VALUE_TEXT}
            data-testid="invoiceSummary.poNumber"
          />
          <SummaryGridItem
            name="Reference"
            value={reference ? reference : UNSET_VALUE_TEXT}
            data-testid="invoiceSummary.reference"
          />
        </SummaryGrid>
      }
      goToStep="TERMS"
      data-testid="invoiceSummary.customFields"
    />
  )
}
